import { Alert, Box, Button, Grid, MenuItem, TextField } from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { authService } from '../../services/api/authService';

const StyledTextField = styled(TextField)`
  & .MuiOutlinedInput-root {
    background: #1a1f24;
    color: #fff;
    border-radius: 8px;
    & fieldset {
      border-color: #42515E;
    }
    &:hover fieldset {
      border-color: #2563EB;
    }
    &.Mui-focused fieldset {
      border-color: #2563EB;
    }
  }
  & .MuiInputLabel-root {
    color: #b0b8c1;
  }
  & .MuiInputLabel-root.Mui-focused {
    color: #2563EB;
  }
`;

const StyledButton = styled(Button)`
  background: #2563EB;
  color: #fff;
  font-weight: 700;
  border-radius: 8px;
  padding: 12px 0;
  &:hover {
    background: #1d4ed8;
  }
`;

const COUNTRIES = [
  { code: 'US', label: 'United States' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'CA', label: 'Canada' },
  { code: 'NG', label: 'Nigeria' },
  { code: 'IN', label: 'India' },
  { code: 'DE', label: 'Germany' },
  { code: 'FR', label: 'France' },
  { code: 'KE', label: 'Kenya' },
  { code: 'GH', label: 'Ghana' },
  // Add more as needed
];

export const RegisterForm = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await authService.register({
        name: `${firstName} ${lastName}`.trim(),
        email,
        phone,
        password,
        country,
      });
      navigate('/login'); // Redirect to login page after successful registration
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during registration');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Grid container spacing={{ xs: 1, sm: 2 }}>
        <Grid size={{xs:12, sm:6}}>
          <StyledTextField
            margin="normal"
            required
            fullWidth
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={isLoading}
          />
        </Grid>
          <Grid size={{xs:12, sm:6}}>
          <StyledTextField
            margin="normal"
            required
            fullWidth
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={isLoading}
          />
        </Grid>
        <Grid size={{xs:12, sm:6}}>
          <StyledTextField
            margin="normal"
            required
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
          />
        </Grid>
        <Grid size={{xs:12, sm:6}}>
          <StyledTextField
            margin="normal"
            required
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            helperText=""
            FormHelperTextProps={{ sx: { color: '#b0b8c1' } }}
          />
        </Grid>
        <Grid size={{xs:12, sm:6}}>
          <MuiTelInput
            margin="normal"
            required
            fullWidth
            label="Phone"
            value={phone}
            onChange={setPhone}
            disabled={isLoading}
            defaultCountry="US"
            sx={{
              mt: 2,
              background: '#1a1f24',
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': {
                  borderColor: '#42515E',
                },
                '&:hover fieldset': {
                  borderColor: '#2563EB',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2563EB',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#b0b8c1',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#2563EB',
              },
            }}
          />
        </Grid>
        <Grid size={{xs:12, sm:6}}>
          <StyledTextField
            margin="normal"
            required
            select
            fullWidth
            label="Country/Region"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            disabled={isLoading}
          >
            <MenuItem value="" disabled>
              Nationality
            </MenuItem>
            {COUNTRIES.map((option) => (
              <MenuItem key={option.code} value={option.label}>
                {option.label}
              </MenuItem>
            ))}
          </StyledTextField>
        </Grid>
      </Grid>
      <StyledButton
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 3, mb: 2 }}
        disabled={isLoading}
      >
        {isLoading ? 'Creating Account...' : 'Sign Up'}
      </StyledButton>
    </Box>
  );
}; 